/**
 * MISSION: Single source of truth for all PostHog event names.
 * CONTEXT_ANCHORS: PostHog analytics, churn-predictor EF, admin dashboards.
 *
 * Regla: NUNCA PII (email, teléfono, payment data) en properties.
 * Regla: user_id llega implícito desde posthog.identify() en AuthGuard.
 */

export const ANALYTICS_EVENTS = {
    // === F1 — Attendance ===
    CLASS_JOIN_CLICKED:       'class_join_clicked',
    CLASS_JOIN_BLOCKED:       'class_join_blocked',
    CLASS_ATTENDED_CONFIRMED: 'class_attended_confirmed', // server-side desde confirm-session EF

    // === F2 — Feedback ===
    CLASS_FEEDBACK_PROMPTED:  'class_feedback_prompted',
    CLASS_FEEDBACK_SUBMITTED: 'class_feedback_submitted',
    CLASS_FEEDBACK_DISMISSED: 'class_feedback_dismissed',

    // === F3 — Baseline engagement ===
    APP_OPENED:       'app_opened',
    LESSON_STARTED:   'lesson_started',
    LESSON_COMPLETED: 'lesson_completed',
    STREAK_EXTENDED:  'streak_extended',
    SPRINT_STARTED:   'sprint_started',
    MAP_VIEWED:       'map_viewed',
    LOGIN_SUCCESS:    'login_success',
    LOGIN_SESSION_CHECK_FAILED: 'login_session_check_failed',
    LOGIN_RESET_FAILED:         'login_reset_failed',

    // === Student door (trailer + returning mark) ===
    DOOR_VIEWED:              'door_viewed',
    DOOR_RETURNING_SHOWN:     'door_returning_shown',
    DOOR_SOUND_ON:            'door_sound_on',
    DOOR_AUTOPLAY_BLOCKED:    'door_autoplay_blocked',
    DOOR_STORAGE_UNAVAILABLE: 'door_storage_unavailable',

    // === Subscriptions realtime (migrated from AppLayout direct import) ===
    SUBSCRIPTION_STATUS_REALTIME_RECEIVED: 'subscription_status_realtime_received',

    // === Placement test funnel ===
    PLACEMENT_TEST_STARTED:    'placement_test_started',
    PLACEMENT_CARD_ANSWERED:   'placement_card_answered',
    PLACEMENT_LEAD_GATE_SHOWN: 'placement_lead_gate_shown',
    PLACEMENT_LEAD_SUBMITTED:  'placement_lead_submitted',
    PLACEMENT_FINALIZED:       'placement_finalized',
} as const

export type AnalyticsEventName = typeof ANALYTICS_EVENTS[keyof typeof ANALYTICS_EVENTS]

// ─── Property shapes (documentación de contrato, no enforcement runtime) ──────

export interface ClassJoinClickedProps {
    readonly session_id: string
    readonly cohort_id: string | null
    readonly lesson_id?: string
    readonly device: 'mobile' | 'desktop'
    readonly seconds_to_session_start: number | null
}

export interface ClassJoinBlockedProps {
    readonly session_id: string
    readonly reason: 'no_meet_link' | 'out_of_window'
}

export interface ClassFeedbackPromptedProps {
    readonly session_id: string
    readonly time_since_class_end_minutes: number
}

export interface ClassFeedbackSubmittedProps {
    readonly session_id: string
    readonly rating_avg: number
    readonly has_free_text: boolean
    readonly n_questions_answered: number
}

export interface ClassFeedbackDismissedProps {
    readonly session_id: string
    readonly reason: 'closed' | 'skipped'
}

// ─── Placement test funnel (no PII — never email/name/phone) ──────────────────

export interface PlacementTestStartedProps {
    readonly source: 'login_bulb' | 'enrollment_level_zero' | 'enrollment_unsure'
}

export interface PlacementCardAnsweredProps {
    readonly skill: string
    readonly cefr_target: string
    readonly score_band: 'strong' | 'developing' | 'needs_support'
    readonly step_index: number
}

export interface PlacementLeadGateShownProps {
    readonly total_items_answered: number
}

// No properties today — the shape exists for symmetry with the other 4 events and as a
// documented extension point (e.g. time spent on the lead gate before submitting).
export type PlacementLeadSubmittedProps = Record<string, never>

export interface PlacementFinalizedProps {
    readonly cefr_level: string
    readonly review_required: boolean
}
